import { Scope } from "@sentry/react";
import dayjs from "dayjs";

const MAX_APP_CRASHED_REFRESHES = 3;
const BETWEEN_REFRESH_MIN_DELAY_SECONDS = 3;
const RESET_INCREMENT_MINUTES = 10;

const refreshOnErrorBoundaryKey = "refreshOnErrorBoundary";

type RefreshOnErrorBoundary = {
    count: number;
    date: string;
};

const parseRefreshOnErrorBoundary = ({ raw }: { raw: string | null }): { success: true; data: RefreshOnErrorBoundary } | { success: false } => {
    if (raw === null) return { success: false };
    try {
        const value: unknown = JSON.parse(raw);
        if (typeof value !== "object" || value === null) return { success: false };
        const { count, date } = value as { count?: unknown; date?: unknown };
        const coercedCount = typeof count === "string" ? Number(count) : count;
        if (typeof coercedCount !== "number" || Number.isNaN(coercedCount)) return { success: false };
        if (typeof date !== "string") return { success: false };
        return { success: true, data: { count: coercedCount, date } };
    } catch {
        return { success: false };
    }
};

export const isChunkLoadError = (error: Error): boolean => {
    return error.name.includes("ChunkLoadError");
};

export const isDynamicImportError = (error: unknown): boolean => {
    if (error instanceof Error === false) return false;

    return error.message.includes("Failed to fetch dynamically imported module");
};

/**
 * Sets item in local storage and reload the page without cache
 * @param count default to 1
 */
const reloadWindowWithoutCache = (count = 1) => {
    localStorage.setItem(
        refreshOnErrorBoundaryKey,
        JSON.stringify({
            count,
            date: dayjs().toDate().toISOString(),
        }),
    );
    // eslint-disable-next-line no-self-assign
    window.location.href = window.location.href;
};

export const handleErrorBoundary = (scope: Scope, error: unknown) => {
    if (!error || !isDynamicImportError(error)) return;

    const refreshOnErrorBoundary = localStorage.getItem(refreshOnErrorBoundaryKey);
    const parsedRefreshOnErrorBoundary = parseRefreshOnErrorBoundary({ raw: refreshOnErrorBoundary });

    if (parsedRefreshOnErrorBoundary.success === false) {
        reloadWindowWithoutCache();
        return;
    }

    if (
        parsedRefreshOnErrorBoundary.data.count <= MAX_APP_CRASHED_REFRESHES &&
        dayjs().isAfter(dayjs(parsedRefreshOnErrorBoundary.data.date).add(BETWEEN_REFRESH_MIN_DELAY_SECONDS, "second"))
    ) {
        reloadWindowWithoutCache(parsedRefreshOnErrorBoundary.data.count + 1);
        return;
    }

    if (dayjs().isAfter(dayjs(parsedRefreshOnErrorBoundary.data.date).add(RESET_INCREMENT_MINUTES, "minute"))) {
        reloadWindowWithoutCache();
    }
};
